import { toPlainText } from '@portabletext/react';
import { imageUrl } from 'api';
import Button from 'components/ui/Button';
import { RecipeValueFactory } from 'factories/recipeValueFactory';
import { useTranslation } from 'hooks/useTranslation';

// import { useTranslation } from 'hooks/useTranslation';
import { FC, useEffect, useState } from 'react';
import { useAppSelector } from 'store/hooks';
import { selectRoomState, selectSelectedRoomOptions } from 'store/roomSlice';
import { NormalizedRecipeValues, NormalizedValue, RecipeValues } from 'type';
import { RecipeCategoryTexts } from 'type/recipe';

import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindConfig from '../../../tailwind.config.js';
import { Config } from 'tailwindcss';
import { useGoogleTagManager } from '@grace-studio/graceful-next/hooks';

// @ts-expect-error: Unreachable code error
const fullConfig = resolveConfig(tailwindConfig as Config);

type PdfLinkType = {
  recipeLink?: string;
  className?: string;
};

type RoomSettingsType = {
  label: string;
  value: string;
};

export type PdfDataProps = {
  title: string;
  preamble: string;
  roomImage: string;
  link: string;
  recipeLink: string;
  productsLink: string;
  solution: RoomSettingsType;
  control: RoomSettingsType;
  presence: RoomSettingsType;
  normalizedValues: NormalizedRecipeValues;
  graphs: {
    texts: {
      low: string;
      high: string;
    };
    sections: {
      header: string;
      subtitle: string;
      description: string;
      text: string;
      progressColors: string[];
      value: NormalizedValue;
    }[];
  };
};

const PdfLink: FC<PdfLinkType> = ({ recipeLink }) => {
  // const translation = useTranslation();
  const currentRoom = useAppSelector(selectRoomState) || {};
  const { recipe } = currentRoom;
  const currentRoomOptions = useAppSelector(selectSelectedRoomOptions);
  const translation = useTranslation();
  const gtm = useGoogleTagManager();
  const [errorMessage, setErrorMessage] = useState<string>();
  const combinedValues =
    currentRoomOptions && RecipeValueFactory.combineValues(currentRoomOptions);
  const normalizedValues =
    currentRoom &&
    combinedValues &&
    RecipeValueFactory.normalizeValues(currentRoom, combinedValues);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setErrorMessage(undefined);
    }, 3000);
  }, [errorMessage]);

  const getNormalizedValue = (value: number) => {
    const normalizedValue = Math.max(Math.ceil(value), 1) as NormalizedValue;

    return normalizedValue;
  };

  const getSectionDescription = (
    category: keyof Omit<RecipeValues, 'controlType'>,
  ) => {
    const texts = recipe.texts[category] as RecipeCategoryTexts;
    const normalizedValue = getNormalizedValue(normalizedValues[category]);

    return toPlainText(texts[`text${normalizedValue}`]);
  };

  const getSectionText = (
    category: keyof Omit<RecipeValues, 'controlType'>,
  ) => {
    const texts = recipe.texts[category] as RecipeCategoryTexts;

    return toPlainText(texts.general);
  };

  const { colors } = fullConfig.theme as any;

  const themeColors = colors as {
    'human-1': string;
    'human-2': string;
    'human-3': string;
    'human-4': string;
    'human-5': string;
    'financial-1': string;
    'financial-2': string;
    'financial-3': string;
    'financial-4': string;
    'financial-5': string;
    'sustainability-1': string;
    'sustainability-2': string;
    'sustainability-3': string;
    'sustainability-4': string;
    'sustainability-5': string;
  };

  const pdfData =
    recipe &&
    ({
      title: recipe.title,
      roomImage: imageUrl(
        currentRoomOptions.lightingSolution?.image,
      ).toString(),
      preamble: toPlainText(recipe.preamble),
      recipeLink,
      normalizedValues,
      productsLink: translation('links.products'),

      solution: {
        label: translation('room.controls.lightingSolution'),
        value: currentRoomOptions.lightingSolution.title,
      },
      control: {
        label: translation('room.controls.adjustments'),
        value: currentRoomOptions.controlType,
      },
      presence: {
        label: translation('room.optionsCategory.presenceLevel'),
        value: currentRoomOptions.adjustments?.['presence-level']?.title,
      },

      graphs: {
        texts: {
          low: translation('recipe.values.low'),
          high: translation('recipe.values.high'),
        },
        sections: [
          {
            header: translation('recipe.categories.sustainability'),
            subtitle: translation('recipe.values.co2'),
            // description: toPlainText(
            //   recipe.texts.co2[`text${getTextFromValue(normalizedValues.co2)}`],
            // ),
            description: getSectionDescription('co2'),
            text: getSectionText('co2'),
            progressColors: [
              themeColors['sustainability-1'],
              themeColors['sustainability-2'],
              themeColors['sustainability-3'],
              themeColors['sustainability-4'],
              themeColors['sustainability-5'],
            ],
            value: normalizedValues.co2,
          },
          {
            header: translation('recipe.categories.human'),
            subtitle: translation('recipe.values.visual'),
            description: getSectionDescription('visual'),
            text: getSectionText('visual'),
            progressColors: [
              themeColors['human-1'],
              themeColors['human-2'],
              themeColors['human-3'],
              themeColors['human-4'],
              themeColors['human-5'],
            ],
            value: normalizedValues.visual,
          },
          {
            header: translation('recipe.categories.financial'),
            subtitle: translation('recipe.values.luminaireCost'),
            description: getSectionDescription('luminaireCost'),
            text: getSectionText('luminaireCost'),
            progressColors: [
              themeColors['financial-1'],
              themeColors['financial-2'],
              themeColors['financial-3'],
              themeColors['financial-4'],
              themeColors['financial-5'],
            ],
            value: normalizedValues.luminaireCost,
          },
          {
            subtitle: translation('recipe.values.energyCost'),
            description: getSectionDescription('energyCost'),
            text: getSectionText('energyCost'),
            progressColors: [
              themeColors['financial-1'],
              themeColors['financial-2'],
              themeColors['financial-3'],
              themeColors['financial-4'],
              themeColors['financial-5'],
            ],
            value: normalizedValues.energyCost,
          },
        ],
      },
    } as PdfDataProps);

  return (
    <Button
      loading={loading}
      onClick={async () => {
        setLoading(true);
        gtm.trackEvent({
          event: 'btn_download_pdf',
        });

        try {
          const response = await fetch('/api/create-pdf', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(pdfData),
          });
          // console.log('response from pdf', response);
          // const blob = await response.blob();
          // const url = URL.createObjectURL(blob);
          // window.open(url);
          // Create a Blob URL
          const url = window.URL.createObjectURL(
            new Blob([await response.blob()]),
          );
          const link = document.createElement('a');
          link.href = url;

          // Set the file name for download
          link.setAttribute('download', 'recipe-of-light.pdf');

          // Trigger download
          document.body.appendChild(link);
          link.click();

          // Clean up
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          setLoading(false);

          // const data = await response.json();
        } catch (error) {
          console.error(error);
          setLoading(false);
          setErrorMessage(translation('recipe.sharing.error'));
        }
      }}
    >
      {translation('downloadPdf')}
      {errorMessage && <div className="ml-2">{errorMessage}</div>}
    </Button>
  );
};

export default PdfLink;
